import { alpha, Box, ButtonBase, css, keyframes, styled } from '@mui/material';

const REDUCED_MOTION_QUERY = '@media (prefers-reduced-motion: reduce)';

const DIM_Z_INDEX = 1250;
const CARD_Z_INDEX = 1260;

const RECORD_DOT_COLOR = '#f5333f';

const DIM_COLOR = 'rgba(16, 24, 40, 0.4)';
const DIM_SHADOW = `0 0 0 200vmax ${DIM_COLOR}`;
const GLOW_REST = '0 6px 18px -8px rgba(11, 11, 207, 0.28), 0 0 0 6px rgba(11, 11, 207, 0.06)';
const GLOW_PEAK = '0 10px 24px -8px rgba(11, 11, 207, 0.36), 0 0 0 11px rgba(11, 11, 207, 0.035)';

const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: none; }
`;

const spotGlow = keyframes`
  0%, 100% { box-shadow: ${GLOW_REST}, ${DIM_SHADOW}; }
  50% { box-shadow: ${GLOW_PEAK}, ${DIM_SHADOW}; }
`;

const arrowDraw = keyframes`
  0% { stroke-dashoffset: 170; opacity: 0; }
  14% { opacity: 0.95; }
  55% { stroke-dashoffset: 0; opacity: 0.95; }
  84% { stroke-dashoffset: 0; opacity: 0.95; }
  100% { stroke-dashoffset: 0; opacity: 0; }
`;

const ringPulse = keyframes`
  0% { transform: translate(-50%, -50%) scale(0.55); opacity: 0.8; }
  70% { transform: translate(-50%, -50%) scale(1.7); opacity: 0; }
  100% { transform: translate(-50%, -50%) scale(1.7); opacity: 0; }
`;

const recBlink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
`;

export const SDim = styled(Box)`
  position: fixed;
  z-index: ${DIM_Z_INDEX};
  inset: 0;
  background: transparent;
`;

export const SSpotlight = styled(Box)`
  position: fixed;
  z-index: ${DIM_Z_INDEX};
  pointer-events: none;
  box-shadow: ${GLOW_REST}, ${DIM_SHADOW};
  animation:
    ${fadeIn} 0.25s ease,
    ${spotGlow} 2.4s ease-in-out infinite;

  ${REDUCED_MOTION_QUERY} {
    animation: none;
  }
`;

export const SCard = styled('dialog')(
  () => css`
    position: fixed;
    right: auto;
    bottom: auto;
    z-index: ${CARD_Z_INDEX};
    width: 300px;
    max-width: none;
    margin: 0;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    animation: ${fadeUp} 0.3s ease;

    &:focus {
      outline: none;
    }

    ${REDUCED_MOTION_QUERY} {
      animation: none;
    }
  `,
);

export const SArrow = styled('svg')(
  ({ theme }) => css`
    position: absolute;
    top: -12px;
    left: -78px;
    color: ${theme.palette.primary[400]};
    pointer-events: none;

    path {
      animation: ${arrowDraw} 3.6s ${EASE} infinite;
    }

    ${REDUCED_MOTION_QUERY} {
      path {
        animation: none;
        opacity: 0.95;
      }
    }
  `,
);

export const SPanel = styled(Box)(
  ({ theme }) => css`
    position: relative;
    padding: 15px 16px 16px;
    border: 1px solid ${theme.palette.grey[200]};
    border-radius: 16px;
    background-color: ${theme.palette.common.white};
    box-shadow:
      0 18px 44px -18px ${alpha(theme.palette.grey[900], 0.34)},
      0 2px 8px -4px ${alpha(theme.palette.grey[900], 0.12)};
  `,
);

export const SDismissButton = styled(ButtonBase)(
  ({ theme }) => css`
    position: absolute;
    top: 9px;
    right: 9px;
    width: 26px;
    height: 26px;
    border-radius: 8px;
    color: ${theme.palette.grey[400]};
    transition:
      background-color 0.12s ease,
      color 0.12s ease;

    &:hover {
      background-color: ${theme.palette.grey[100]};
      color: ${theme.palette.grey[700]};
    }

    &:focus-visible {
      outline: 2px solid ${theme.palette.primary[500]};
      outline-offset: -2px;
    }
  `,
);

export const SContent = styled(Box)`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding-right: 18px;
`;

export const SRecordIcon = styled('span')(
  ({ theme }) => css`
    position: relative;
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: 12px;
    background-color: ${theme.palette.grey[50]};
  `,
);

export const SRecordRing = styled('span')`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 15px;
  height: 15px;
  border: 2px solid ${RECORD_DOT_COLOR};
  border-radius: 50%;
  opacity: 0;
  animation: ${ringPulse} 2.8s ease-out infinite;

  & + & {
    animation-delay: 1.4s;
  }

  ${REDUCED_MOTION_QUERY} {
    display: none;
  }
`;

export const SRecordDot = styled('span')`
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background-color: ${RECORD_DOT_COLOR};
  box-shadow: 0 0 0 3px rgba(245, 51, 63, 0.16);
  animation: ${recBlink} 2.8s ease-in-out infinite;

  ${REDUCED_MOTION_QUERY} {
    animation: none;
  }
`;

export const STitle = styled('h2')(
  ({ theme }) => css`
    margin: 0;
    font-family: ${theme.typography.fontFamily};
    font-size: 15px;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: -0.01em;
    color: ${theme.palette.grey[900]};
    text-wrap: pretty;
  `,
);

export const SText = styled('p')(
  ({ theme }) => css`
    margin: 5px 0 0;
    font-family: ${theme.typography.fontFamily};
    font-size: 13px;
    font-weight: 400;
    line-height: 19px;
    color: ${theme.palette.grey[500]};
    text-wrap: pretty;
  `,
);
